import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  CustomLoading,
  ModalConfirmV2,
  ModalSizeVariants,
  RadioButtonsV2,
  RadioButtonType,
  useAuth,
  RadioButtonGroupMapping,
  SelectV2,
  LabraToolTipV2,
} from '@labrav/react-components'
import { RootState, useDispatch, useSelector } from '../../store'
import {
  actionTypeWrapper,
  CloudAccountChangeParams,
  CloudAccountChangeV2,
  CloudType,
  extractAccountIdFromCombinedId,
  getSelectedAccountIdWithCloud,
  providerToAuthCloud,
  setSelectedAccount,
  useAccountId,
  useCurrentCloudProvider,
  useCloudAccounts,
} from '@labrav/common'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faExternalLinkAlt,
  faListCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { SelectChangeEvent } from '@mui/material/Select'
import { Box, Divider } from '@mui/material'
import { isEmpty } from 'lodash'
import {
  clearProductsListing,
  setSelectedProductOnListing,
} from '../../modules/productsListing/actionCreators'
import { setCloudMarketplaceProductListing } from '../../modules/productListingFilter/action'
import { getProducts } from '../../modules/productsListing/action'
import { ProductStatus, type CloudMarketplace } from '../../types'
import { CustomProductSelectorDropdown } from '../../common/components/CustomProductSelectorDropdown/CustomProductSelectorDropdown'
import { FlyoutFunction } from '../../modules/productsListing/types'
import {
  getMarketplaceReferralTemplatesInfo,
  setMarketplaceTemplatesInfo,
} from '../../modules/marketplaceMappingManager/action'
import type { MarketplaceTemplatesDetailsData } from '../../modules/marketplaceMappingManager/types'
import { loadAvailableAccounts } from '../../components/FlyoutSettings/accountSelectionActions'
import { useFlag } from '@labrav/flags'
import { useStyles } from './CreateModalHubspotWrapper.styles'
import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { CRMTypes } from '../../utils/constants'
import { isResaleProduct } from '../../utils/privateOfferOrResaleHelperFunctions'

const creationType = [
  {
    key: 'PRIVATE_OFFER',
    value: 'Private offer',
    label: 'Private offer',
    type: RadioButtonType.STANDARD,
  },
  {
    key: 'RESELLER_AUTH',
    value: 'Reseller authorization',
    label: 'Reseller authorization',
    type: RadioButtonType.STANDARD,
  },
]
export const CreateModalHubspotWrapper = () => {
  const {
    cloud: routeCloud,
    accountId: routeAccountId,
    partnerId,
    secureKey: secureKeyFromURL,
  } = useParams<{
    cloud?: string
    accountId?: string
    partnerId?: string
    secureKey?: string
  }>()

  const { classes } = useStyles()
  const navigate = useNavigate()
  const { search } = useLocation()
  const dispatch = useDispatch()

  const [open, setOpen] = useState(true)
  const [selectedCreationType, setSelectedCreationType] =
    useState<string>('PRIVATE_OFFER')

  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')
  const [isDataLoaded, setIsDataLoaded] = useState(false)

  const templatesData = useSelector(
    (state: RootState) => state.marketplaceMappingManager?.templateDetails || []
  )
  const crmId = useSelector<string | undefined>(
    state => state.AccountData.user.partnerData?.crmId || ''
  )
  const availableAccounts = useSelector((state: RootState) => {
    const accounts = state.accountSelection?.availableAccounts
    return Array.isArray(accounts) ? accounts : []
  })

  const { flag: microsoftResellerAuthorizationFlag } = useFlag(
    'microsoftResellerAuthorization'
  )
  const { flag: hubspotAzureResellerAuthFlag } = useFlag(
    'hubspotAzureResellerAuth'
  )
  const {
    flag: azureHubspotMappingManagerFlag,
    loading: azureHubspotMappingManagerFlagLoading,
  } = useFlag(
    'azureHubspotMappingManager'
  )
  const isMicrosoftResellerAuthorizationEnabled =
    !!microsoftResellerAuthorizationFlag?.value
  const isHubspotAzureResellerAuthEnabled =
    !!hubspotAzureResellerAuthFlag?.value
  const isAzureHubspotMappingManagerEnabled =
    !!azureHubspotMappingManagerFlag?.value

  const selectedCloudTypeProductListing = useCurrentCloudProvider({
    microsoftResellerAuthorization: isMicrosoftResellerAuthorizationEnabled,
  })
  const accountIdFromSelection = useAccountId({ product: 'flyout' }) || ''
  const currentAccountId = routeAccountId || accountIdFromSelection
  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    currentAccountId,
    'flyout',
    routeCloud || selectedCloudTypeProductListing
  )
  const currentCloud = (
    routeCloud ||
    selectedCloudTypeProductListing ||
    ''
  ).toUpperCase()
  const isTemplateSelectionDisabled =
    selectedCreationType === 'RESELLER_AUTH' ||
    currentCloud === 'GCP' ||
    (currentCloud === 'AZURE' && !isAzureHubspotMappingManagerEnabled)
  const { products } = useFlyOutProductsBasedOnSelectedCloud({
    microsoftResellerAuthorization: isMicrosoftResellerAuthorizationEnabled,
  })

  const selectedProduct = useMemo(
    () => products.find(product => product.productId === selectedProductId),
    [products, selectedProductId]
  )
  const selectedProductStatus = selectedProduct?.status
  const isSelectedProductEligibleForResellerAuth =
    !selectedProduct || isResaleProduct(selectedProduct)

  // HubSpot Azure MPPO is supported when microsoftResellerAuthorization is on
  // (same gate as ExternalNewResaleAuthorization). GCP stays unsupported.
  const isResellerAuthDisabledForCloud =
    currentCloud === 'GCP' ||
    (currentCloud === 'AZURE' &&
      (!isMicrosoftResellerAuthorizationEnabled ||
        !isHubspotAzureResellerAuthEnabled))

  const creationTypeOptions = useMemo(() => {
    const resellerAuthDisabled =
      isResellerAuthDisabledForCloud ||
      !isSelectedProductEligibleForResellerAuth
    const disabledTooltip = isResellerAuthDisabledForCloud
      ? currentCloud === 'GCP'
        ? 'Reseller authorization is not supported for GCP cloud account.'
        : currentCloud === 'AZURE'
          ? 'HubSpot Azure reseller authorization is disabled.'
          : 'Reseller authorization is not supported for Microsoft cloud account.'
      : !isSelectedProductEligibleForResellerAuth
        ? 'Reseller authorization is not available for Professional Services or Container products.'
        : undefined
    return creationType.map(option => {
      if (option.key !== 'RESELLER_AUTH') {
        return option
      }
      return {
        ...option,
        disabled: resellerAuthDisabled,
        disabledTooltip,
      }
    })
  }, [
    isResellerAuthDisabledForCloud,
    isSelectedProductEligibleForResellerAuth,
    currentCloud,
    isHubspotAzureResellerAuthEnabled,
    isMicrosoftResellerAuthorizationEnabled,
  ])

  const { canDo, loading: authLoading } = useAuth()

  const baseMarketplaceCloudAccounts = useCloudAccounts('flyout')
  const marketplaceCloudAccounts = useMemo(() => {
    if (authLoading) return []
    // Create-offer flow: require marketplace *create* on each account (stricter than list *view*).
    return baseMarketplaceCloudAccounts.map(account => {
      const hasPermission = canDo({
        cloud: providerToAuthCloud(account.provider),
        service: 'marketplace',
        action: 'create',
      })

      return {
        ...account,
        disabled: !hasPermission,
      }
    })
  }, [authLoading, baseMarketplaceCloudAccounts, canDo])

  const fetchProductsAndTemplates = useCallback(
    async (accountId: string, cloud: CloudMarketplace) => {
      const requests: Promise<unknown>[] = [
        dispatch(getProducts(accountId, cloud)) as Promise<unknown>,
      ]
      dispatch(setMarketplaceTemplatesInfo([]))

      if (
        crmId &&
        !(cloud.toUpperCase() === 'AZURE' &&
          !isAzureHubspotMappingManagerEnabled)
      ) {
        requests.push(
          dispatch(
            getMarketplaceReferralTemplatesInfo(
              crmId,
              'flyout',
              CRMTypes.hubspot,
              accountId,
              undefined,
              true,
              cloud
            )
          ) as Promise<unknown>
        )
      }

      await Promise.all(requests)
    },
    [crmId, dispatch, isAzureHubspotMappingManagerEnabled]
  )

  // 1) Cloud accounts first — only when store has no data.
  useEffect(() => {
    if (!partnerId || availableAccounts.length > 0) return
    dispatch(loadAvailableAccounts(partnerId))
  }, [partnerId, availableAccounts.length, dispatch])

  // Default to first flyout account so products/templates can load.
  useEffect(() => {
    if (currentAccountId || marketplaceCloudAccounts.length === 0) return
    const firstAccount = marketplaceCloudAccounts[0]
    if (!firstAccount?.originalId || !firstAccount?.provider) return
    dispatch(
      setSelectedAccount(
        firstAccount.originalId,
        'flyout',
        firstAccount.provider as CloudType
      )
    )
    dispatch(setCloudMarketplaceProductListing(firstAccount.provider))
  }, [currentAccountId, marketplaceCloudAccounts, dispatch])

  // 2) After cloud accounts are ready + account selected: products + templates in parallel.
  // Also re-runs when cloud/account changes.
  useEffect(() => {
    if (availableAccounts.length === 0) return
    if (isEmpty(currentAccountId) || !selectedCloudTypeProductListing) return
    if (
      selectedCloudTypeProductListing.toUpperCase() === 'AZURE' &&
      azureHubspotMappingManagerFlagLoading
    ) {
      setIsDataLoaded(false)
      return
    }

    let cancelled = false
    const run = async () => {
      setIsDataLoaded(false)
      setSelectedTemplate('')
      await fetchProductsAndTemplates(
        currentAccountId,
        selectedCloudTypeProductListing
      )
      if (!cancelled) {
        setIsDataLoaded(true)
      }
    }
    run()

    return () => {
      cancelled = true
    }
  }, [
    availableAccounts.length,
    currentAccountId,
    selectedCloudTypeProductListing,
    fetchProductsAndTemplates,
    azureHubspotMappingManagerFlagLoading,
  ])

  const handleTemplateSelectChange = (event: SelectChangeEvent) => {
    if (isTemplateSelectionDisabled) return
    setSelectedTemplate(event.target.value as string)
  }

  // Clear template when selection is unavailable for this cloud and offer type.
  useEffect(() => {
    if (isTemplateSelectionDisabled && selectedTemplate) {
      setSelectedTemplate('')
    }
  }, [isTemplateSelectionDisabled, selectedTemplate])

  const handleCloudAccountChange = useCallback(
    async (params: CloudAccountChangeParams) => {
      const accountId = extractAccountIdFromCombinedId(params.accountId)
      const { cloud } = params

      const pathname = `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerId}/create-marketplace-offer`
      navigate({ pathname, search: location.search }, { replace: true })

      setIsDataLoaded(false)
      setSelectedProductId('')
      setSelectedTemplate('')

      await dispatch(
        setSelectedAccount(accountId, 'flyout', cloud as CloudType)
      )
      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(undefined)
        )
      )
      await dispatch(actionTypeWrapper(cloud, clearProductsListing()))
      await dispatch(setCloudMarketplaceProductListing(cloud))
      // products + templates re-fetched by the account/cloud useEffect above
    },
    [
      dispatch,
      navigate,
      selectedCloudTypeProductListing,
      secureKeyFromURL,
      partnerId,
    ]
  )

  const handleProductChangeLocal = useCallback(
    (productId: string) => {
      setSelectedProductId(productId)

      const nextProduct = products.find(
        product => product.productId === productId
      )
      if (
        selectedCreationType === 'RESELLER_AUTH' &&
        nextProduct &&
        !isResaleProduct(nextProduct)
      ) {
        setSelectedCreationType('PRIVATE_OFFER')
      }
    },
    [products, selectedCreationType]
  )

  const handleAccept = useCallback(async () => {
    if (
      isEmpty(selectedProductId) ||
      isEmpty(currentAccountId) ||
      !secureKeyFromURL ||
      !partnerId
    ) {
      return
    }

    const cloudPath = (
      routeCloud || selectedCloudTypeProductListing
    ).toLowerCase()

    const pathSuffix =
      selectedCreationType === 'RESELLER_AUTH'
        ? 'resale-authorizations/edit'
        : 'private-offers/edit'

    const pathname = `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerId}/cloud/${cloudPath}/accounts/${currentAccountId}/product-listing/${selectedProductId}/${pathSuffix}`

    const newSearchParams = new URLSearchParams(search)
    // Mark entry from this modal so EditPrivateOfferWrapperV2 skips ChooseTemplateModal
    // (template was already offered here, and is optional).
    newSearchParams.set('from', 'createModal')
    if (selectedTemplate && !isTemplateSelectionDisabled) {
      newSearchParams.set('templateId', selectedTemplate)
    } else {
      newSearchParams.delete('templateId')
    }

    setOpen(false)
    await navigate({
      pathname,
      search: `?${newSearchParams.toString()}`,
    })
  }, [
    selectedCreationType,
    selectedProductId,
    currentAccountId,
    secureKeyFromURL,
    partnerId,
    routeCloud,
    selectedCloudTypeProductListing,
    search,
    selectedTemplate,
    isTemplateSelectionDisabled,
    navigate,
  ])

  const isContinueDisabled =
    isEmpty(currentAccountId) ||
    isEmpty(selectedProductId) ||
    selectedProductStatus === ProductStatus.PRODUCT_STATUS_RESTRICTED ||
    (selectedCreationType === 'RESELLER_AUTH' &&
      !isSelectedProductEligibleForResellerAuth)

  const disabledAcceptButtonMessage = useMemo(() => {
    if (selectedProductStatus === ProductStatus.PRODUCT_STATUS_RESTRICTED) {
      return 'Offer creation is not allowed by AWS for this restricted product.'
    }
    if (
      selectedCreationType === 'RESELLER_AUTH' &&
      !isSelectedProductEligibleForResellerAuth
    ) {
      return 'Reseller authorization is not available for Professional Services or Container products.'
    }
    if (isEmpty(currentAccountId) && isEmpty(selectedProductId)) {
      return 'Select a cloud account and product to continue.'
    }
    if (isEmpty(currentAccountId)) {
      return 'Select a cloud account to continue.'
    }
    if (isEmpty(selectedProductId)) {
      return 'Select a product to continue.'
    }
    return ''
  }, [
    currentAccountId,
    selectedProductId,
    selectedProductStatus,
    selectedCreationType,
    isSelectedProductEligibleForResellerAuth,
  ])

  const handleCreationTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextCreationType = event.target.value
      const canSelectResellerAuth =
        !isResellerAuthDisabledForCloud &&
        !(selectedProduct && !isResaleProduct(selectedProduct))

      // RadioButtonsV2 clears value when the same option is clicked —
      // with only two options, switch to the other (if allowed).
      if (!nextCreationType) {
        if (selectedCreationType === 'PRIVATE_OFFER' && canSelectResellerAuth) {
          setSelectedCreationType('RESELLER_AUTH')
        } else {
          setSelectedCreationType('PRIVATE_OFFER')
        }
        return
      }

      // Do not allow selecting Reseller auth for unsupported cloud or product types.
      if (nextCreationType === 'RESELLER_AUTH' && !canSelectResellerAuth) {
        return
      }
      setSelectedCreationType(nextCreationType)
    },
    [selectedCreationType, selectedProduct, isResellerAuthDisabledForCloud]
  )

  useEffect(() => {
    if (
      selectedCreationType === 'RESELLER_AUTH' &&
      (isResellerAuthDisabledForCloud ||
        (selectedProduct && !isResaleProduct(selectedProduct)))
    ) {
      setSelectedCreationType('PRIVATE_OFFER')
    }
  }, [selectedCreationType, selectedProduct, isResellerAuthDisabledForCloud])

  const originURL = window.location.origin
  const handleOpenTemplateInNewTab =
    (templateId: string) => (e: React.MouseEvent) => {
      e.stopPropagation()
      window.open(
        `${originURL}/settings/mappings/marketplace/templates/${templateId}?viewOnly=true`,
        '_blank'
      )
    }

  return (
    <ModalConfirmV2
      title="Select to proceed"
      icon={
        <FontAwesomeIcon
          icon={faListCheck}
          style={{ height: '16px', width: '16px' }}
        />
      }
      content={
        isDataLoaded ? (
          <div className={classes.modalContentWrapper}>
            <div className={classes.modalContentTitle}>
              Select the transaction type, product and template to continue
            </div>
            <div className={classes.radioButtonsWrapper}>
              <RadioButtonsV2
                checkedValue={{
                  key: selectedCreationType ?? '',
                  value: selectedCreationType,
                }}
                radioButtonMappings={
                  creationTypeOptions as RadioButtonGroupMapping[]
                }
                handleChange={handleCreationTypeChange}
                gapBetween="16px"
              />
            </div>

            <div className={classes.selectionWrapper}>
              <Divider />

              <CloudAccountChangeV2
                cloudAccounts={marketplaceCloudAccounts}
                selectedAccountId={selectedAccountIdWithCloud as string}
                onChange={handleCloudAccountChange}
                width={220}
                size="small"
              />
              <CustomProductSelectorDropdown
                functionType={
                  selectedCreationType === 'PRIVATE_OFFER'
                    ? FlyoutFunction.PRIVATE_OFFERS
                    : FlyoutFunction.RESALE_AUTHORIZATIONS
                }
                onProductChange={handleProductChangeLocal}
                openNewPrivateOfferModal={open}
              />
              <LabraToolTipV2
                tooltipPlacement="bottom"
                description={
                  isTemplateSelectionDisabled
                    ? 'Template selection is not available for this cloud account.'
                    : ''
                }
              >
                <div
                  className={
                    isTemplateSelectionDisabled
                      ? classes.disabledSelectWrapper
                      : undefined
                  }
                >
                  <SelectV2
                    field={{
                      title: 'Select a template (optional)',
                      placeHolder: 'Select a template (optional)',
                      options: templatesData.map(
                        (template: MarketplaceTemplatesDetailsData) => ({
                          value: template?.templateId,
                          label: template?.templateName,
                          menuItemAction: (
                            <FontAwesomeIcon
                              className={classes.externalLinkIcon}
                              icon={faExternalLinkAlt}
                              onClick={handleOpenTemplateInNewTab(
                                template.templateId
                              )}
                            />
                          ),
                        })
                      ),
                      isReadOnly: isTemplateSelectionDisabled,
                    }}
                    value={selectedTemplate ?? ''}
                    onChange={handleTemplateSelectChange}
                    onBlur={() => {}}
                    isError={false}
                    errorText="create-template"
                    size="small"
                    viewOnly={isTemplateSelectionDisabled}
                    disabled={isTemplateSelectionDisabled}
                  />
                </div>
              </LabraToolTipV2>
            </div>
          </div>
        ) : (
          <Box className={classes.box}>
            <CustomLoading
              loadingTitle="Products are being fetched"
              loadingSubTitle="It may take a few moments, please wait..."
            />
          </Box>
        )
      }
      open={open}
      onAccept={handleAccept}
      onDecline={() => {}}
      titleAcceptButton="Continue"
      variant={ModalSizeVariants.medium}
      acceptDisabled={!isDataLoaded || isContinueDisabled}
      disabledAcceptButtonMessage={disabledAcceptButtonMessage}
      hideCancelButton={true}
      hideCancelIcon={true}
    />
  )
}
